/**
 * training-groups-desktop.js
 */

import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory, Link } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { UserContext } from 'contexts/user/user-context';
import { Types } from 'utils/constants';
import FeatureBox from 'components/feature-box';
import Button from 'components/button';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu';
import IconTitle from 'components/icon-title';
import CaptionedSvg from 'components/captioned-svg';
import GroupListDesktop from '../group-list-desktop';
import GroupName from '../group-name';
import RequestType from '../request-type';
import { TrainingGroupsContext } from './training-groups-context';
import { trainingGroupRoute, canLeaveGroup } from './training-groups';
import TrainingGroupAdd from './training-group-add';
import { ReactComponent as TeamCollabSvg } from 'assets/images/team-collaboration.svg';


const useStyles = makeStyles(theme => ({
  copy: {
    color: theme.palette.sys.onSurfaceVariant,
    fontSize: theme.typography.sizes[2],
    cursor: 'pointer',
  }
}));


export default function TrainingGroupsDesktop(props) {
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const {
    groups,
    onDeleteRequest,
    onLeaveGroup,
    onDeleteGroup,
  } = React.useContext(TrainingGroupsContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const columns = [{
    field: 'name',
    headerName: 'NAME',
    flex: 20,
    renderCell: params => <GroupName 
      name={params.value} 
      decorators={<>
        {/* REQUEST TYPE */}
        {params.row.membership && !params.row.membership.dateAccepted &&
        <RequestType context='training-group' request={params.row.membership} />}
      </>}
    />
  }, {
    field: 'dateCreated',
    headerName: 'CREATED',
    flex: 9,
    valueGetter: params => parseISO(params.value),
    renderCell: params => params.value.toLocaleDateString()
  }, {
    field: 'code',
    headerName: 'GROUP CODE',
    flex: 8,
    renderCell: params => <>
      {params.value} 
      <Tooltip arrow placement='top' title='Copy to clipboard'>
        <span className='flex-hc' style={{ marginLeft: 8 }}>
          <Icon type='copy' className={classes.copy} onClick={e => onCopyToClipboard(e, params.value)} />
        </span>
      </Tooltip>
    </>
  }, {
    field: 'handler-count-or-action',
    headerName: 'HANDLERS',
    flex: 7,
    type: 'number',
    valueGetter: params => params.row.members?.length || '-',
  }, {
    field: 'menu',
    headerName: ' ',
    sortable: false,
    align: 'right',
    flex: 8,
    renderCell: params => {
      const canLeave = canLeaveGroup(params.row, user);
      const canDelete = user.role.isTrainer || params.row.membership?.isLeader;
      let items = [];

      // VIEW
      if (user.role.isTrainer || params.row.membership.dateAccepted || params.row.membership.type === Types.MemberRequest.TO_USER) {
        items.push({
          key: 'view',
          name: <b>View</b>,
          icon: 'group-training',
          component: Link,
          to: trainingGroupRoute(params.row.id, history)
        });
      }

      // LEAVE
      if (user.role.isHandler && params.row.membership.dateAccepted) {
        items.push({
          key: 'leave',
          name: 'Leave ...',
          icon: 'group-leave',
          disabled: !canLeave,
          tooltip: !canLeave ? 'You cannot leave a group as the only leader' : '',
          onSelect: () => onLeaveGroup(params.row, setIsLoading)
        });
      }

      // DELETE
      if (user.role.isTrainer || params.row.membership.dateAccepted) {
        items.push({
          key: 'delete',
          name: 'Delete ...',
          icon: 'delete',
          disabled: !canDelete,
          tooltip: !canDelete ? 'You must be a leader to delete a group' : '',
          onSelect: () => onDeleteGroup(params.row, setIsLoading)
        });
      }

      // REMOVE
      if (user.role.isHandler && !params.row.membership.dateAccepted) {
        items.push({
          key: 'remove',
          name: 'Remove ...',
          icon: 'close',
          onSelect: () => onDeleteRequest(params.row, params.row.membership.code, setIsLoading)
        });
      }      

      return (
        <DropMenu
          id={`${params.row.id}-menu`}
          label={<IconButton size='small'><Icon type='menu' /></IconButton>}
          items={items}
        />
      );
    }
  }];


  const rowUrl = (group) => {
    return (user.role.isTrainer || group.membership?.dateAccepted || group.membership?.type === Types.MemberRequest.TO_USER)
      ? trainingGroupRoute(group.id, history)
      : null;
  }


  const onCopyToClipboard = async (e, value) => {
    e.stopPropagation();
    e.preventDefault();
    await navigator.clipboard.writeText(value);
    enqueueSnackbar(<>Copied '<b>{value}</b>' to clipboard</>, { variant: 'success' });
  }


  return (
    <>
      <FeatureBox loading={isLoading}>
        <IconTitle
          iconType='group-training'
          title='Training Groups'
          gutterBottom
          subtitle={<>
            {user.role.isHandler && <>A training group is a set of handlers with which you commonly train. The list below shows each group you've joined as well as any outstanding join requests.</>}
            {user.role.isTrainer && <>A training group is a set of handlers that commonly train together. Your handlers' training groups are listed below.</>}
          </>}
          actions={<TrainingGroupAdd
            menuLabel={<Button
              variant='contained'
              children='Training Group'
              startIcon={<Icon type='add' />}
              endIcon={<Icon type='arrow-down' />}
            />}
          />}
        />

        {groups.length === 0 &&
        <CaptionedSvg
          image={<TeamCollabSvg />}
          text={<>
            {user.role.isHandler && <>You don't belong to any training groups. Use the <b>+ Training Group</b> button to get connected with a group of handlers.</>}
            {user.role.isTrainer && <>There are no training groups because you aren't training anyone. Use the <b>+ Member</b> button on the Manage page to invite a handler to be managed.</>}
          </>}
        />}

        {groups.length > 0 &&
        <GroupListDesktop
          width={845}
          rows={groups}
          rowUrl={rowUrl}
          columns={columns}
        />}
      </FeatureBox>
    </>
  )
}